import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, PrinterIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import SEO from '@/components/SEO';
import PSEODisclaimer from '@/components/seo/PSEODisclaimer';
import { fetchChecklistEntry, type ChecklistItem } from '@/services/pseoService';

const BASE_URL = 'https://seniorthrive.com';

const PriorityBadge = ({ priority }: { priority: string }) => {
  const colors: Record<string, string> = {
    immediate: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
    essential: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
    'before-discharge': 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200',
    'day-of': 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200',
    'day-1': 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
    'week-1': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200',
    'week-1-2': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200',
    'week-2': 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
    high: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200',
    medium: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
    'month-1': 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
    'long-term': 'bg-gray-100 text-gray-800 dark:bg-gray-950 dark:text-gray-200',
    ongoing: 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-200',
    first: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
    'as-needed': 'bg-gray-100 text-gray-800 dark:bg-gray-950 dark:text-gray-200',
  };
  const label = priority.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${colors[priority] || colors.medium}`}>
      {label}
    </span>
  );
};

const ChecklistSection = ({
  title,
  pillarColor,
  items,
}: {
  title: string;
  pillarColor: string;
  items: ChecklistItem[];
}) => (
  <section className="mb-10">
    <h2 className={`text-2xl font-bold mb-4 ${pillarColor}`}>{title}</h2>
    <div className="space-y-3">
      {items.map((item, i) => (
        <label key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer print:bg-white print:border print:border-gray-200">
          <input
            type="checkbox"
            className="mt-1 h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary shrink-0"
          />
          <div className="flex-1 min-w-0">
            <span className="text-foreground leading-relaxed">{item.item}</span>
            {item.priority && (
              <div className="mt-1">
                <PriorityBadge priority={item.priority} />
              </div>
            )}
          </div>
        </label>
      ))}
    </div>
  </section>
);

const ChecklistPage = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: entry, isLoading, error } = useQuery({
    queryKey: ['checklist-entry', slug],
    queryFn: () => fetchChecklistEntry(slug!),
    enabled: !!slug,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  if (isLoading) {
    const checklistName = slug
      ? slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      : 'Checklist';

    return (
      <>
        <SEO
          title={`${checklistName} Checklist: What to Do Right Now`}
          description={`Free printable ${checklistName.toLowerCase()} checklist for seniors and families. Step-by-step guide covering safety, wellness, and family coordination.`}
          canonical={`${BASE_URL}/checklists/${slug}`}
        />
        <div className="py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <Skeleton className="h-4 w-24 mb-4" />
            <Skeleton className="h-12 w-full mb-4" />
            <Skeleton className="h-4 w-48 mb-8" />
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error || !entry) {
    return (
      <div className="py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-4xl font-bold text-foreground mb-4">Checklist Not Found</h1>
          <p className="text-muted-foreground mb-8">
            The checklist you're looking for doesn't exist or has been moved.
          </p>
          <Button asChild>
            <Link to="/checklists">
              <ArrowLeftIcon className="mr-2 w-4 h-4" />
              Back to Checklists
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const pageUrl = `${BASE_URL}/checklists/${entry.slug}`;
  const metaTitle = entry.seo_meta?.metaTitle || `${entry.title}: What to Do Right Now`;
  const metaDescription = entry.seo_meta?.metaDescription || entry.why_it_matters.substring(0, 160);
  const metaKeywords = entry.seo_meta?.keywords?.join(', ') || '';
  const totalItems = entry.safety_items.length + entry.wellness_items.length + entry.connection_items.length;

  const categoryLabels: Record<string, string> = {
    'life-event': 'Life Event',
    'room-by-room': 'Room by Room',
    caregiver: 'Caregiver',
    seasonal: 'Seasonal',
    'condition-specific': 'Condition-Specific',
  };

  const breadcrumbs = [
    { name: 'Home', url: BASE_URL },
    { name: 'Checklists', url: `${BASE_URL}/checklists` },
    { name: entry.title, url: pageUrl },
  ];

  // HowTo schema for Featured Snippet eligibility
  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: entry.title,
    description: entry.why_it_matters,
    totalTime: `PT${Math.ceil(totalItems * 2)}M`,
    step: [
      ...entry.safety_items.map((item, i) => ({
        '@type': 'HowToStep',
        position: i + 1,
        name: `Safety: ${item.item.substring(0, 80)}`,
        text: item.item,
      })),
      ...entry.wellness_items.map((item, i) => ({
        '@type': 'HowToStep',
        position: entry.safety_items.length + i + 1,
        name: `Wellness: ${item.item.substring(0, 80)}`,
        text: item.item,
      })),
      ...entry.connection_items.map((item, i) => ({
        '@type': 'HowToStep',
        position: entry.safety_items.length + entry.wellness_items.length + i + 1,
        name: `Connection: ${item.item.substring(0, 80)}`,
        text: item.item,
      })),
    ],
  };

  return (
    <>
      <SEO
        title={metaTitle}
        description={metaDescription}
        keywords={metaKeywords}
        url={pageUrl}
        canonical={pageUrl}
        type="article"
        breadcrumbs={breadcrumbs}
        article={{
          publishedTime: entry.published_at || entry.created_at,
          modifiedTime: entry.updated_at,
          author: 'SeniorThrive Team',
          section: categoryLabels[entry.category] || entry.category,
        }}
      />
      {/* HowTo Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }} />

      <div className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          {/* Top nav */}
          <div className="mb-8 flex items-center justify-between">
            <Button variant="ghost" asChild>
              <Link to="/checklists">
                <ArrowLeftIcon className="mr-2 w-4 h-4" />
                All Checklists
              </Link>
            </Button>
            {entry.printable && (
              <Button variant="outline" onClick={() => window.print()} className="print:hidden">
                <PrinterIcon className="mr-2 w-4 h-4" />
                Print Checklist
              </Button>
            )}
          </div>

          <article>
            {/* Header */}
            <header className="mb-10">
              {entry.category && (
                <Badge variant="secondary" className="mb-4">
                  {categoryLabels[entry.category] || entry.category}
                </Badge>
              )}
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 leading-tight">
                {entry.title}
              </h1>
              <p className="text-lg text-muted-foreground">
                {totalItems} items across Safety, Wellness, and Connection
              </p>
            </header>

            {/* Why It Matters */}
            <section className="mb-10">
              <div className="bg-amber-50 dark:bg-amber-950/30 rounded-xl p-6 border border-amber-100 dark:border-amber-900">
                <h2 className="text-lg font-bold text-foreground mb-2">Why This Matters</h2>
                <p className="text-muted-foreground leading-relaxed">{entry.why_it_matters}</p>
              </div>
            </section>

            {/* Three Pillar Checklists */}
            <ChecklistSection
              title="Safety Checklist"
              pillarColor="text-red-700 dark:text-red-400"
              items={entry.safety_items}
            />
            <ChecklistSection
              title="Wellness Checklist"
              pillarColor="text-blue-700 dark:text-blue-400"
              items={entry.wellness_items}
            />
            <ChecklistSection
              title="Connection Checklist"
              pillarColor="text-green-700 dark:text-green-400"
              items={entry.connection_items}
            />

            {/* What to Do Next */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-foreground mb-4">What to Do Next</h2>
              <p className="text-muted-foreground leading-relaxed text-lg whitespace-pre-line">{entry.what_to_do_next}</p>
            </section>

            {/* Professional Resources */}
            {entry.professional_resources && (
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-foreground mb-4">Professional Resources</h2>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{entry.professional_resources}</p>
              </section>
            )}

            {/* Related Checklists */}
            {entry.related_checklist_slugs.length > 0 && (
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-foreground mb-4">Related Checklists</h2>
                <div className="flex flex-wrap gap-2">
                  {entry.related_checklist_slugs.map((checkSlug) => (
                    <Link
                      key={checkSlug}
                      to={`/checklists/${checkSlug}`}
                      className="inline-flex items-center px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm font-medium text-foreground transition-colors"
                    >
                      <CheckCircleIcon className="w-4 h-4 mr-2" />
                      {checkSlug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Related Learning */}
            {entry.related_glossary_slugs.length > 0 && (
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-foreground mb-4">Learn More</h2>
                <div className="flex flex-wrap gap-2">
                  {entry.related_glossary_slugs.map((termSlug) => (
                    <Link
                      key={termSlug}
                      to={`/learn/${termSlug}`}
                      className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-950/50 text-sm font-medium text-blue-800 dark:text-blue-200 transition-colors border border-blue-200 dark:border-blue-800"
                    >
                      {termSlug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* CTA, drives to free trial, not just quiz */}
            <section className="bg-gradient-to-r from-blue-50 to-teal-50 dark:from-blue-950/30 dark:to-teal-950/30 rounded-xl p-8 text-center border border-blue-100 dark:border-blue-900 print:hidden">
              <h2 className="text-2xl font-bold text-foreground mb-3">
                Track This Checklist (and More) in SeniorThrive
              </h2>
              <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                {entry.category === 'life-event'
                  ? 'SeniorThrive helps families coordinate care after life events like this. Daily check-ins, medication tracking, care team coordination, and a shared view of how recovery is going.'
                  : entry.category === 'room-by-room'
                  ? 'SeniorThrive includes a built-in Home Safety Check that walks through every room. Track which modifications are done, what is next, and share progress with the whole family.'
                  : entry.category === 'caregiver'
                  ? 'SeniorThrive takes tasks off the caregiver\'s plate. Daily wellness signals replace anxious check-in calls, and the whole care team can see how things are going without one person being the bottleneck.'
                  : 'SeniorThrive brings safety, wellness, and family connection into one place. Free to start, no credit card required.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild size="lg">
                  <a
                    href={`https://app.seniorthrive.com/auth?signup=true&utm_source=checklists&utm_medium=pseo&utm_campaign=${entry.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Start Your Free Trial
                  </a>
                </Button>
                <Button variant="outline" asChild size="lg">
                  <Link to="/features">See All Features</Link>
                </Button>
              </div>
            </section>
          </article>

          {/* Disclaimer, shared across all pSEO pages */}
          <PSEODisclaimer lastUpdated={entry.updated_at} className="mt-12 print:hidden" />

          {/* Footer nav */}
          <div className="border-t pt-8 mt-12 print:hidden">
            <Button asChild>
              <Link to="/checklists">
                <ArrowLeftIcon className="mr-2 w-4 h-4" />
                Back to All Checklists
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChecklistPage;
